import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Paper } from '@mui/material';
import { HotTable } from '@handsontable/react-wrapper';
import { registerAllModules } from 'handsontable/registry';
import Handsontable from 'handsontable';

import {
  mapErrorMessageToColumnIndex,
  validateCellError,
  validateDropdownValue,
  convertFromTableDataPreserveString,
  formatNumberValue,
} from '../../hooks/use-excel-table';
import { useTableHeight } from '../../hooks/use-table-height';
import {
  get3LineColumns,
  get3LineNestedHeaders,
  createWrappedRowColumnTable,
  MaxLengthEditor,
} from '../common/handsontable.config';
import {
  handleMerchandiseColumn,
  createContextMenuSettings,
  THREE_LINE_FORMULATION_PRODUCT_TYPE_COL,
  tryOpenMerchandisePopupOnEnter,
} from '../common/handsontable-helpers';
import { autocompleteRenderer } from '../materials/autocompletes/autocomplete-in-excel';
import { dropdownRenderer } from '../materials/dropdowns/dropdown-in-excel';
import { getColIndexFromCellChange } from '../common/handsontable.types';

import {
  ensureWrappedRowColumnTableLength,
  wrappedRowIndex,
  mapOneLineValidationColumnTo3LineCell,
  PLANT_COLUMN_INDEX,
  STT_COLUMN_INDEX,
  EVENT_CLICK_MOUSE,
} from './handsontable-three-line.utils';
import {
  convert3LineTo1LineTableDataPreserveString,
  convertTo3LineTableData,
} from './handsontable-three-line.converters';

import type { WrappedRowColumn } from '../common/handsontable.config';
import type {
  CellChange,
  CellProperties,
  CellPropsBase,
  ChangeSource,
  ExtendedContextMenuSettings,
  HandsontableColumnSettings,
  HotTableRef,
  MergeConfig,
  PlanTableData,
  RendererFunction,
  RowsWithErrors,
  SelectionPayload,
} from '../common/handsontable.types';

import { type SearchOption } from '@/types/search';
import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';
import '../../styles/handsontable.styles.css';
import { env } from '@/config/env';

registerAllModules();

Handsontable.editors.registerEditor('maxLengthEditor', MaxLengthEditor);

/**
 * Validate and normalize 3-line format data
 */
const validateAndNormalizeThreeLine = (
  data: PlanTableData,
  rowsPerPlan: number,
  plantOptions: SearchOption[],
  witnessCompanyOptions: SearchOption[]
): PlanTableData | null => {
  if (data.length === 0 || data.length % rowsPerPlan !== 0) {
    return null;
  }

  try {
    return convert3LineTo1LineTableDataPreserveString(data, plantOptions, witnessCompanyOptions);
  } catch {
    return null;
  }
};

/**
 * Check if data looks like 1-line format (20+ columns)
 */
const isOneLineFormat = (data: PlanTableData): boolean => {
  const firstRow = data[0];
  return firstRow && Array.isArray(firstRow) && firstRow.length >= 20;
};

// Re-export converters for backward compatibility
export {
  convertTo3LineTableData,
  convertFrom3LineTableData,
} from './handsontable-three-line.converters';

// Re-export types
export type { HandsontableThreeLineProps } from './handsontable-three-line.types';

export interface HandsontableThreeLineSimpleProps {
  tableData?: PlanTableData;
  onDataChange?: (data: PlanTableData) => void;
  plantOptions?: SearchOption[];
  witnessCompanyOptions?: SearchOption[];
  rowsWithErrors?: RowsWithErrors;
  onGetTableData?: (getData: () => PlanTableData) => void;
  onAddRowAbove?: (index: number) => void;
  onAddRowBelow?: (index: number) => void;
  onDeleteRows?: (indices: number[]) => void;
  setRowsWithErrors?: (rowsWithErrors: RowsWithErrors) => void;
}

/**
 * Simple 3-line Excel table component
 * Based on original HandsontableCommon but customized for 3-line format
 */
export const HandsontableThreeLine: React.FC<HandsontableThreeLineSimpleProps> = ({
  plantOptions: propsPlantOptions = [],
  witnessCompanyOptions: propsWitnessCompanyOptions = [],
  tableData: propsTableData,
  rowsWithErrors = [],
  onDataChange,
  onGetTableData,
  onAddRowAbove,
  onAddRowBelow,
  onDeleteRows,
  setRowsWithErrors,
}) => {
  // Get list of license keys from env
  const licenseKeys = env.HANDSONTABLE_LICENSE_KEY ?? 'non-commercial-and-evaluation';

  const hotTableRef = useRef<Handsontable | null>(null);
  const isMountedRef = useRef(true);
  const isUpdatingRef = useRef<boolean>(false);
  const previousRowCountFromPropsRef = useRef<number>(0);

  const plantOptions = propsPlantOptions;
  const witnessCompanyOptions = propsWitnessCompanyOptions;

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (hotTableRef.current && !hotTableRef.current.isDestroyed) {
        hotTableRef.current.destroy();
      }
      hotTableRef.current = null;
    };
  }, []);

  // Safe getter for Handsontable instance
  const getHot = useCallback(() => {
    if (!isMountedRef.current) return null;
    const hot = hotTableRef.current;
    if (!hot || hot.isDestroyed) return null;
    return hot;
  }, []);

  // Create wrapped row column table for line 2 and line 3
  const wrappedRowColumnTable = useMemo(
    () =>
      ensureWrappedRowColumnTableLength(
        createWrappedRowColumnTable(plantOptions, witnessCompanyOptions)
      ),
    [plantOptions, witnessCompanyOptions]
  );
  const rowsPerPlan = wrappedRowColumnTable.length + 1; // line 1 + wrapped rows

  // Convert rowsWithErrors to cellErrors format for 3-line
  // Map column indices from 1-line format to 3-line format
  const cellErrors = useMemo(() => {
    if (!rowsWithErrors || rowsWithErrors.length === 0) return [];

    return rowsWithErrors.flatMap((row) => {
      const planIndex = row.rowIndex;
      const line1RowIndex = planIndex * rowsPerPlan;
      return row.field
        .map((field) => {
          const targetCell = mapOneLineValidationColumnTo3LineCell(
            field.index,
            line1RowIndex,
            rowsPerPlan
          );
          if (!targetCell) return null;
          return {
            row: targetCell.row,
            col: targetCell.col,
            comment: { value: field.message },
          };
        })
        .filter((cell): cell is { row: number; col: number; comment: { value: string } } => {
          return cell !== null;
        });
    });
  }, [rowsWithErrors, rowsPerPlan]);

  const tableData = propsTableData ?? [];

  // Load data from props - only reload when row count changes
  useEffect(() => {
    const currentRowCount = propsTableData?.length || 0;
    const previousRowCount = previousRowCountFromPropsRef.current;

    if (propsTableData && propsTableData.length > 0) {
      if (previousRowCount === 0 || currentRowCount !== previousRowCount) {
        const hot = getHot();
        if (!hot) return;

        hot.loadData(propsTableData);
        previousRowCountFromPropsRef.current = currentRowCount;
      }
    }
  }, [propsTableData?.length, getHot]);

  // Update columns when options change
  useEffect(() => {
    const hot = getHot();
    if (!hot) return;

    const hasPlantOptions = plantOptions.length > 0;
    const hasWitnessOptions = witnessCompanyOptions.length > 0;

    if (hasPlantOptions || hasWitnessOptions) {
      const currentColumns = hot.getSettings().columns;
      if (Array.isArray(currentColumns)) {
        const currentData = hot.getData();

        const updatedColumns = currentColumns.map((col, index) => {
          // Column 2: ブランド (Plant) - in 3-line format
          if (index === 2 && col.type === 'autocomplete' && hasPlantOptions) {
            const newSource = plantOptions.map((opt) => opt.label);
            return {
              ...col,
              source: newSource,
              renderer: autocompleteRenderer,
            };
          }
          return col;
        });

        hot.updateSettings({
          columns: updatedColumns,
        });

        hot.loadData(currentData);

        setTimeout(() => {
          const safeHot = getHot();
          if (safeHot) {
            safeHot.render();
          }
        }, 0);
      }
    }
  }, [plantOptions, witnessCompanyOptions, getHot]);

  // Map 3-line column to 1-line column for validation
  const getOneLineColumnIndex = useCallback((wrappedRowIdx: number, col: number) => {
    if (wrappedRowIdx === -1) {
      // Line 1: Map 3-line column to 1-line column
      return col - 1; // 3-line col 1 -> 1-line col 0, etc.
    }
    if (wrappedRowIdx === 0) {
      // Line 2: Map to corresponding 1-line columns (13-21)
      const line2To1LineMap: Record<number, number> = {
        1: 12, // vehicleSize
        2: 13, // precedingMaterialType
        3: 14, // precedingMaterialQuantity
        4: 15, // pitchHope
        6: 16, // pitchPossible
        7: 17, // placingMethod
        8: 18, // witnessCompanyId
        10: 19, // witnessName
        12: 20, // witnessTel
      };
      return line2To1LineMap[col] ?? null;
    }
    if (wrappedRowIdx === 1) {
      // Line 3: 備考 社内 / 備考 ゼネコン → 1-line cols 21–22
      if (col === 1) return 21; // remarks (社内)
      if (col === 7) return 22; // remarksForClient (ゼネコン)
    }
    return null;
  }, []);

  // Schedule table re-render after changes
  const scheduleRender = useCallback(
    (hot: Handsontable) => {
      const activeEditor = hot.getActiveEditor();
      const isEditorOpen = activeEditor?.isOpened();

      if (isEditorOpen) {
        isUpdatingRef.current = false;
      } else {
        setTimeout(() => {
          const safeHot = getHot();
          if (safeHot) {
            safeHot.render();
          }
          setTimeout(() => {
            isUpdatingRef.current = false;
          }, 50);
        }, 50);
      }
    },
    [getHot]
  );

  // Extract plan data collection logic to reduce complexity
  const collectPlanData = useCallback(
    (startPlanIndex: number, endPlanIndex: number): PlanTableData => {
      const actualData: PlanTableData = [];

      for (let planIndex = startPlanIndex; planIndex <= endPlanIndex; planIndex++) {
        const planStartRow = planIndex * rowsPerPlan;
        const planEndRow = planStartRow + rowsPerPlan - 1;

        for (let row = planStartRow; row <= planEndRow && row < tableData.length; row++) {
          const rowData = tableData[row] ? [...tableData[row]] : [];
          actualData.push(rowData);
        }
      }

      return actualData;
    },
    [tableData, rowsPerPlan]
  );

  // Extract conversion logic to reduce complexity
  const convertPlanDataToClipboard = useCallback(
    (actualData: PlanTableData): PlanTableData => {
      return convert3LineTo1LineTableDataPreserveString(
        actualData,
        plantOptions,
        witnessCompanyOptions
      );
    },
    [plantOptions, witnessCompanyOptions]
  );

  // Handle copy operations - convert 3-line format to 1-line format
  // When copying from any cell in a row, copy the entire plan (all 3 lines)
  const handleBeforeCopy = useCallback(
    (data: PlanTableData, _coords: unknown[]): void => {
      const hot = getHot();
      if (!hot) {
        return;
      }

      try {
        const selection = hot.getSelected();
        if (!selection || selection.length === 0) {
          return;
        }

        const [startRow, , endRow] = selection[0];

        // Determine which plans are selected based on row indices
        const startPlanIndex = Math.floor(startRow / rowsPerPlan);
        const endPlanIndex = Math.floor(endRow / rowsPerPlan);

        // Collect all rows for the selected plans
        const actualData = collectPlanData(startPlanIndex, endPlanIndex);

        if (actualData.length === 0) {
          return;
        }

        // Convert 3-line format to 1-line format for clipboard
        const convertedData = convertPlanDataToClipboard(actualData);
        data.length = 0;
        data.push(...convertedData);
      } catch {
        // Silently handle errors
      }
    },
    [getHot, collectPlanData, convertPlanDataToClipboard, rowsPerPlan]
  );

  // Handle paste operations - convert pasted data to 3-line format
  const handleBeforePaste = useCallback(
    (data: PlanTableData, _coords: unknown[]): void => {
      const hot = getHot();
      if (!hot || !data || data.length === 0) {
        return;
      }

      try {
        // Check if pasted data is 1-line format (20+ columns)
        if (isOneLineFormat(data)) {
          // Convert 1-line format to 3-line format
          // Step 1: Convert 1-line table data to plans
          const plans = convertFromTableDataPreserveString(
            data,
            plantOptions,
            witnessCompanyOptions
          );
          // Step 2: Convert plans to 3-line format
          const convertedData = convertTo3LineTableData(plans, plantOptions, witnessCompanyOptions);
          data.length = 0;
          data.push(...convertedData);
          return;
        }

        // For 3-line format data, validate and normalize
        // to ensure data consistency
        try {
          if (data.length > 0 && data.length % rowsPerPlan === 0) {
            // Data is already in correct 3-line format, just normalize it
            const normalizedData = validateAndNormalizeThreeLine(
              data,
              rowsPerPlan,
              plantOptions,
              witnessCompanyOptions
            );
            if (normalizedData) {
              // Convert back to 3-line format
              const plans = convertFromTableDataPreserveString(
                normalizedData,
                plantOptions,
                witnessCompanyOptions
              );
              const convertedData = convertTo3LineTableData(
                plans,
                plantOptions,
                witnessCompanyOptions
              );
              data.length = 0;
              data.push(...convertedData);
            }
            return;
          }
        } catch {
          // If validation fails, try to use data as-is
        }
      } catch {
        // Silently handle errors
      }
    },
    [plantOptions, witnessCompanyOptions, rowsPerPlan, getHot]
  );

  // Handle cell changes
  const handleAfterChange = useCallback(
    (changes: (CellChange | null)[] | null, source?: ChangeSource) => {
      if (!changes || changes.length === 0 || source === 'loadData') return;

      const hot = getHot();
      if (!hot) return;

      if (isUpdatingRef.current) return;
      isUpdatingRef.current = true;

      const newTableData: PlanTableData = hot.getData() as PlanTableData;
      const oneLineRowsForValidation = convert3LineTo1LineTableDataPreserveString(
        newTableData,
        plantOptions,
        witnessCompanyOptions
      ) as unknown[][];

      // Batch validate all changes for paste operation
      let currentRowsWithErrors = rowsWithErrors ?? [];

      for (const change of changes) {
        if (!change) continue;
        const [row, prop, , newValue] = change;
        const col = getColIndexFromCellChange(prop);
        if (col == null) continue;

        // Preserve values for all editable columns
        newTableData[row][col] = newValue ?? '';

        // Validate cells in line 1, line 2, and line 3
        if (setRowsWithErrors) {
          const wrappedRowIdx = wrappedRowIndex(row, wrappedRowColumnTable.length);
          const planIndex = Math.floor(row / rowsPerPlan);
          const col1Line = getOneLineColumnIndex(wrappedRowIdx, col);

          if (col1Line !== null && col1Line >= 0) {
            const oneLineRow = oneLineRowsForValidation[planIndex];
            const errorMessage = validateCellError(
              col1Line,
              newValue,
              plantOptions,
              witnessCompanyOptions,
              oneLineRow
            );

            // Update errors in batch for paste operation
            currentRowsWithErrors = mapErrorMessageToColumnIndex(
              currentRowsWithErrors,
              col1Line,
              planIndex,
              errorMessage ?? ''
            );
          }
        }
      }

      // Set all errors at once after validating all changes
      if (setRowsWithErrors && changes.length > 0) {
        setRowsWithErrors(currentRowsWithErrors);
      }

      // Don't call onDataChange here for 3-line format
      // It causes unnecessary conversion that can lose string values (convert to NaN)
      // Parent component should use onGetTableData to get data when needed
      if (onDataChange) {
        onDataChange(newTableData);
      }

      // Schedule re-render
      scheduleRender(hot);
    },
    [
      onDataChange,
      scheduleRender,
      getHot,
      setRowsWithErrors,
      rowsWithErrors,
      wrappedRowColumnTable,
      rowsPerPlan,
      getOneLineColumnIndex,
      plantOptions,
      witnessCompanyOptions,
    ]
  );

  // Determine if row is line 1 (first row of 3-row group)
  const isLine1 = useCallback((row: number) => {
    return row % 3 === 0;
  }, []);

  // Handle cell click - only for line 1 cells
  const handleBeforeOnCellMouseDown = useCallback(
    (event: MouseEvent, coords: { row: number; col: number }) => {
      const hot = getHot();
      if (!hot || coords.row < 0) return;

      // If clicking on STT column, select all 3 rows of the plan
      if (coords.col === STT_COLUMN_INDEX) {
        // Keep current multi-selection when opening context menu with right-click.
        if (event.button === EVENT_CLICK_MOUSE) {
          return;
        }

        const planIndex = Math.floor(coords.row / rowsPerPlan);
        const startRow = planIndex * rowsPerPlan;
        const endRow = startRow + rowsPerPlan - 1;
        const lastCol = hot.countCols() - 1;

        // Select all 3 rows of the plan.
        // Support additive multi-selection (Ctrl/Cmd) and range selection (Shift).
        setTimeout(() => {
          const safeHot = getHot();
          if (safeHot) {
            if (event.shiftKey) {
              const selected = safeHot.getSelected();
              if (selected && selected.length > 0) {
                const anchorRow = selected[0][0];
                const anchorPlanStart = Math.floor(anchorRow / rowsPerPlan) * rowsPerPlan;
                const anchorPlanEnd = anchorPlanStart + rowsPerPlan - 1;
                const rangeStartRow = Math.min(anchorPlanStart, startRow);
                const rangeEndRow = Math.max(anchorPlanEnd, endRow);
                safeHot.selectCell(rangeStartRow, 0, rangeEndRow, lastCol);
                return;
              }
            }

            if (event.ctrlKey || event.metaKey) {
              const selected = safeHot.getSelected() ?? [];
              const existingRanges = selected.map((range) => {
                const [r1, c1, r2, c2] = range;
                return [Math.min(r1, r2), Math.min(c1, c2), Math.max(r1, r2), Math.max(c1, c2)];
              });

              const mergedRanges = [...existingRanges, [startRow, 0, endRow, lastCol]];
              const selectableHot = safeHot as unknown as {
                selectCells?: (coords: number[][]) => void;
              };

              if (typeof selectableHot.selectCells === 'function') {
                selectableHot.selectCells(mergedRanges);
                return;
              }
            }

            safeHot.selectCell(startRow, 0, endRow, lastCol);
          }
        }, 0);
        return;
      }

      // Open merchandise popup on double-click for column 12 (内容) only on line 1 when 種類 (col 11) is 生コン
      if (isLine1(coords.row) && coords.col === 12) {
        handleMerchandiseColumn(event, hot, coords, THREE_LINE_FORMULATION_PRODUCT_TYPE_COL, {
          contentCol: 12,
          formulationProductTypeCol: THREE_LINE_FORMULATION_PRODUCT_TYPE_COL,
          getNextMerchandiseRow: (r) => r + rowsPerPlan,
          isSelectableRow: isLine1,
        });
        return;
      }
      const target = event.target as HTMLElement;
      if (target.classList.contains('dropdown-icon')) {
        event.stopPropagation();
        hot.selectCell(coords.row, coords.col);
        setTimeout(() => {
          const safeHot = getHot();
          if (safeHot) {
            safeHot.getActiveEditor()?.beginEditing();
          }
        }, 400);
      }
    },
    [isLine1, getHot, rowsPerPlan]
  );

  // Handle before context menu - show for row headers and STT column
  const handleBeforeOnCellContextMenu = useCallback(
    (_event: MouseEvent, coords: { row: number; col: number }) => {
      // Show context menu for row headers (col === -1) or STT column (col === 0)
      if (coords.col !== -1 && coords.col !== STT_COLUMN_INDEX) {
        _event.stopImmediatePropagation();
        return false;
      }
      return true;
    },
    []
  );

  // Transform row index to plan index for context menu operations
  const getRowIndexFromSelection = useCallback((selection: SelectionPayload): number => {
    if (selection.length === 0) {
      return 0;
    }
    const firstRange = selection[0];
    if (Array.isArray(firstRange)) {
      return typeof firstRange[0] === 'number' ? firstRange[0] : 0;
    }
    return typeof firstRange.start?.row === 'number' ? firstRange.start.row : 0;
  }, []);

  const getContextMenuPlanIndex = useCallback(
    (selection: SelectionPayload) => {
      const rowIndex = getRowIndexFromSelection(selection);
      return Math.floor(rowIndex / rowsPerPlan);
    },
    [getRowIndexFromSelection, rowsPerPlan]
  );

  // Transform row index for add above - find the start row of the plan
  const getRowIndexForAddAbove = useCallback(
    (selection: SelectionPayload) => {
      const rowIndex = getRowIndexFromSelection(selection);
      // Find the start row of the plan (line 1)
      const planIndex = Math.floor(rowIndex / rowsPerPlan);
      return planIndex * rowsPerPlan;
    },
    [getRowIndexFromSelection, rowsPerPlan]
  );

  // Transform row index for add below - find the start row of the next plan
  const getRowIndexForAddBelow = useCallback(
    (selection: SelectionPayload) => {
      const rowIndex = getRowIndexFromSelection(selection);
      // Find the start row of the next plan (line 1 of next plan)
      const planIndex = Math.floor(rowIndex / rowsPerPlan);
      return (planIndex + 1) * rowsPerPlan;
    },
    [getRowIndexFromSelection, rowsPerPlan]
  );

  // Context menu configuration using common function - transform row index for 3-line format
  const contextMenuSettings = useMemo(
    () =>
      createContextMenuSettings({
        onAddRowAbove,
        onAddRowBelow,
        onDeleteRows,
        allowSTTColumn: true, // Allow context menu on STT column (col === 0)
        getRowIndexForDelete: getContextMenuPlanIndex, // Transform row index to plan index
        getRowIndexForAddAbove, // Transform row index to start row of plan
        getRowIndexForAddBelow, // Transform row index to start row of next plan
      }),
    [
      onAddRowAbove,
      onAddRowBelow,
      onDeleteRows,
      getContextMenuPlanIndex,
      getRowIndexForAddAbove,
      getRowIndexForAddBelow,
    ]
  );

  // Calculate table height
  const [tableHeight, setTableHeight] = useState<number>(600);
  const containerRef = useRef<HTMLDivElement>(null);

  useTableHeight(setTableHeight);

  // Expose method to get table data
  useEffect(() => {
    if (onGetTableData) {
      onGetTableData(() => {
        const hot = getHot();
        if (!hot) return [];
        return hot.getData() as PlanTableData;
      });
    }
  }, [onGetTableData, getHot]);

  // Column definitions for line 1
  const line1Columns = useMemo(
    () => get3LineColumns(plantOptions, witnessCompanyOptions),
    [plantOptions, witnessCompanyOptions]
  );

  // Apply column settings
  const columns = useMemo(() => {
    return line1Columns.map((col, index) => {
      if (index === STT_COLUMN_INDEX) {
        return {
          ...col,
          title: '',
          className: 'htCenter htMiddle',
        };
      }
      if (index === PLANT_COLUMN_INDEX) {
        return {
          ...col,
          strict: false,
          filter: true,
          allowInvalid: true,
          allowEmpty: true,
          renderer: autocompleteRenderer,
        };
      }
      return col;
    });
  }, [line1Columns]);

  // Nested headers for 3-line format
  const nestedHeaders = useMemo(() => get3LineNestedHeaders(), []);

  // Merge cells configuration for 3-line format
  const mergeCells = useMemo(() => {
    if (!tableData || tableData.length === 0) return [];

    const merges: MergeConfig[] = [];
    const numPlans = Math.floor(tableData.length / rowsPerPlan);

    for (let i = 0; i < numPlans; i++) {
      const startRow = i * rowsPerPlan;

      merges.push(
        // STT column merge removed - each row has its own STT cell
        // Line 2 merges (wrappedRowIdx = 0)
        // Based on original LINE2_MERGES configuration
        {
          row: startRow + 1,
          col: 4,
          rowspan: 1,
          colspan: 2,
        },
        {
          row: startRow + 1,
          col: 8,
          rowspan: 1,
          colspan: 2,
        },
        {
          row: startRow + 1,
          col: 10,
          rowspan: 1,
          colspan: 2,
        },
        // Line 3 merges (wrappedRowIdx = 1, last row)
        // Remarks (col 1) spans cols 1-6
        {
          row: startRow + 2,
          col: 1,
          rowspan: 1,
          colspan: 6,
        },
        // RemarksForClient (col 7) spans cols 7-12
        {
          row: startRow + 2,
          col: 7,
          rowspan: 1,
          colspan: 6,
        }
      );
    }

    return merges;
  }, [tableData, rowsPerPlan]);

  // Render content using appropriate renderer and return innerHTML
  const renderToTempDiv = useCallback(
    (
      rendererFn: RendererFunction,
      instance: Handsontable.Core,
      r: number,
      c: number,
      p: string | number,
      value: unknown,
      cellProperties?: CellProperties
    ) => {
      const tempDiv = document.createElement('div');
      if (cellProperties) {
        rendererFn(instance, tempDiv, r, c, p, value, cellProperties);
      } else {
        rendererFn(instance, tempDiv, r, c, p, value);
      }
      return tempDiv.innerHTML;
    },
    []
  );

  // Render content for wrapped row cells
  const renderWrappedCellContent = useCallback(
    (params: {
      content: HTMLElement;
      instance: Handsontable.Core;
      row: number;
      col: number;
      prop: string | number;
      value: unknown;
      cellProperties: CellProperties;
      column: WrappedRowColumn;
      cellPropsRenderer: RendererFunction | undefined;
    }) => {
      const {
        content,
        instance,
        row,
        col,
        prop,
        value,
        cellProperties,
        column,
        cellPropsRenderer,
      } = params;

      const renderer =
        (column?.props?.renderer as RendererFunction | undefined) || cellProperties.renderer;

      if (renderer && typeof renderer === 'function' && renderer !== cellPropsRenderer) {
        content.innerHTML = renderToTempDiv(
          renderer as RendererFunction,
          instance,
          row,
          col,
          prop,
          value,
          cellProperties
        );
      } else if (cellProperties.type === 'dropdown') {
        content.innerHTML = renderToTempDiv(dropdownRenderer, instance, row, col, prop, value);
      } else if (cellProperties.type === 'autocomplete') {
        content.innerHTML = renderToTempDiv(autocompleteRenderer, instance, row, col, prop, value);
      } else {
        content.textContent = value != null && value !== '' ? String(value) : '';
      }
    },
    [renderToTempDiv]
  );

  // Render STT column cell properties
  const getSTTCellProps = useCallback(
    (wrappedRowIdx: number) => {
      const classNames = ['htCenter', 'htMiddle'];

      if (wrappedRowIdx === -1) {
        classNames.push('plan-line1');
      }
      if (wrappedRowIdx === wrappedRowColumnTable.length - 1) {
        classNames.push('plan-last-line');
      }

      const cellProps: CellPropsBase = {
        readOnly: true,
        className: classNames.join(' '),
      };

      cellProps.renderer = (_instance: Handsontable.Core, td: HTMLElement, r: number) => {
        const idx = wrappedRowIndex(r, wrappedRowColumnTable.length);
        td.style.textAlign = 'center';
        td.style.verticalAlign = 'middle';
        if (idx === -1) {
          const planIndex = Math.floor(r / rowsPerPlan);
          td.textContent = String(planIndex + 1);
        } else {
          td.textContent = '';
        }
        if (idx === wrappedRowColumnTable.length - 1) {
          td.style.setProperty('border-bottom', '1px solid #000000', 'important');
        }
        return td;
      };

      return cellProps;
    },
    [wrappedRowColumnTable, rowsPerPlan]
  );

  // Get schema index for wrapped row column
  const getWrappedRowSchemaIndex = useCallback((wrappedRowIdx: number, col: number) => {
    if (wrappedRowIdx === 1) {
      // Line 3 special mapping
      if (col >= 1 && col <= 6) return 0;
      if (col >= 7 && col <= 12) return 6;
      return null;
    }
    return col - 1;
  }, []);

  // Render wrapped row cell
  const renderWrappedRowCell = useCallback(
    (
      column: WrappedRowColumn,
      cellProps: CellPropsBase,
      params: {
        instance: Handsontable.Core;
        td: HTMLElement;
        row: number;
        col: number;
        prop: string | number;
        value: unknown;
        cellProperties?: CellProperties;
      }
    ) => {
      const {
        instance,
        td,
        row: r,
        col: c,
        prop: p,
        value,
        cellProperties = {} as CellProperties,
      } = params;
      td.innerHTML = '';
      const container = document.createElement('div');
      container.style.cssText = 'display: flex; flex-direction: column; width: 100%; height: 100%;';

      // Header (label)
      const header = document.createElement('div');
      header.style.cssText =
        'background-color: #d4d4d4; padding: 4px 6px; text-align: center; font-size: 13px; font-weight: 700; border-bottom: 1px solid #999;';
      header.textContent = column?.label || '';

      // Content (value)
      const content = document.createElement('div');
      content.style.cssText = 'padding: 5px; flex: 1; overflow: hidden;';

      renderWrappedCellContent({
        content,
        instance,
        row: r,
        col: c,
        prop: p,
        value,
        cellProperties,
        column,
        cellPropsRenderer: cellProps.renderer,
      });

      container.appendChild(header);
      container.appendChild(content);
      td.appendChild(container);

      // Add last line border
      const idx = wrappedRowIndex(r, wrappedRowColumnTable.length);
      if (idx === wrappedRowColumnTable.length - 1) {
        td.style.setProperty('border-bottom', '1px solid #000000', 'important');
      }

      return td;
    },
    [wrappedRowColumnTable, renderWrappedCellContent]
  );

  // Custom cell renderer with wrapped row logic
  const cells = useCallback(
    (row: number, col: number, _prop: string | number) => {
      const wrappedRowIdx = wrappedRowIndex(row, wrappedRowColumnTable.length);

      // STT column special handling
      if (col === STT_COLUMN_INDEX) {
        return getSTTCellProps(wrappedRowIdx);
      }

      // Line 1 cells
      if (wrappedRowIdx === -1) {
        const cellProps: CellPropsBase = { className: 'plan-line1' };
        const line1Col = line1Columns[col];
        if (line1Col) {
          Object.assign(cellProps, line1Col);
        }
        return cellProps;
      }

      // Wrapped rows (Line 2, Line 3, etc.)
      const schemaIndex = getWrappedRowSchemaIndex(wrappedRowIdx, col);
      if (schemaIndex === null) {
        return { readOnly: true };
      }

      const column = wrappedRowColumnTable[wrappedRowIdx]?.[schemaIndex];
      if (!column) {
        return { readOnly: true };
      }

      // Apply column config
      const cellProps: CellPropsBase = {};
      if (column.props) {
        Object.assign(cellProps, column.props);
      }

      // Custom renderer for wrapped rows
      cellProps.renderer = (
        instance: Handsontable.Core,
        td: HTMLElement,
        r: number,
        c: number,
        p: string | number,
        value: unknown,
        cellProperties?: CellProperties
      ) =>
        renderWrappedRowCell(column, cellProps, {
          instance,
          td,
          row: r,
          col: c,
          prop: p,
          value,
          cellProperties,
        });

      // Add last line class
      if (wrappedRowIdx === wrappedRowColumnTable.length - 1) {
        const existingClassName = cellProps.className;
        cellProps.className = `${
          Array.isArray(existingClassName) ? existingClassName.join(' ') : (existingClassName ?? '')
        } plan-last-line`.trim();
      }

      return cellProps;
    },
    [
      wrappedRowColumnTable,
      line1Columns,
      getSTTCellProps,
      getWrappedRowSchemaIndex,
      renderWrappedRowCell,
    ]
  );

  const handleBeforeChange = (changes: (CellChange | null)[] | null, source?: ChangeSource) => {
    if (!changes || changes.length === 0 || source === 'loadData') return;

    const hot = getHot();
    if (!hot) return;

    changes.forEach((change) => {
      if (!change) return;
      const [row, prop, , newValue] = change;
      const col = getColIndexFromCellChange(prop);
      if (col == null) return;
      // remove error highlight from column
      hot.setCellMetaObject(row, col, { comment: undefined });
      const wrappedRowIdx = wrappedRowIndex(row, wrappedRowColumnTable.length);
      const col1Line = getOneLineColumnIndex(wrappedRowIdx, col);
      if (col1Line === null || col1Line < 0) return;
      validateDropdownValue(change, col1Line, newValue);
      formatNumberValue(change, col1Line, newValue);
    });
  };

  // Re-render when errors change
  useEffect(() => {
    const hot = getHot();
    if (!hot) return;
    const timeoutId = setTimeout(() => {
      const safeHot = getHot();
      if (safeHot) {
        safeHot.render();
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [cellErrors, getHot]);

  return (
    <Paper elevation={1} sx={{ p: 2, minHeight: '400px' }}>
      <div ref={containerRef} className='handsontable-container excel-table-3-line'>
        <HotTable
          ref={(hotComponent: HotTableRef | null) => {
            // Unmount
            if (!hotComponent) {
              hotTableRef.current = null;
              return;
            }

            const instance = hotComponent.hotInstance;
            if (!instance || instance.isDestroyed) {
              hotTableRef.current = null;
              return;
            }

            hotTableRef.current = instance;
          }}
          data={tableData}
          cell={cellErrors}
          cells={cells}
          comments={true}
          columns={columns as HandsontableColumnSettings[]}
          beforeKeyDown={(event: KeyboardEvent) => {
            const hot = getHot();
            if (!hot) return;

            const merchandiseHandled = tryOpenMerchandisePopupOnEnter(hot, event, {
              contentCol: 12,
              formulationProductTypeCol: THREE_LINE_FORMULATION_PRODUCT_TYPE_COL,
              isSelectableRow: isLine1,
              getNextMerchandiseRow: (r) => r + rowsPerPlan,
            });
            if (merchandiseHandled) {
              return false;
            }

            if (event.key === 'Enter') {
              const selected = hot.getSelected();
              if (selected && selected.length > 0) {
                const [row, col] = selected[0];
                // In 3-line layout, col 0 is STT, so indices are shifted vs 1-line:
                // col 2 = プラント (plant), col 3 = 打設日 (placement date).
                // The refresh workaround below was meant for 打設日 only; using col === 2
                // wrongly targeted プラント and broke Enter-to-edit when the cell already had a value.
                const isLine1Row = wrappedRowIndex(row, wrappedRowColumnTable.length) === -1;
                const placementDateCol3Line = 3;
                if (isLine1Row && col === placementDateCol3Line) {
                  const cellValue = hot.getDataAtCell(row, col);
                  if (cellValue) {
                    setTimeout(() => {
                      const safeHot = getHot();
                      if (!safeHot) return;
                      safeHot.setDataAtCell(row, col, cellValue);
                      safeHot.render();
                    }, 0);
                  }
                }
              }
            }
          }}
          colHeaders={true}
          rowHeaders={false}
          nestedHeaders={nestedHeaders as Handsontable.GridSettings['nestedHeaders']}
          mergeCells={mergeCells}
          width='100%'
          height={tableHeight}
          licenseKey={licenseKeys}
          beforeCopy={handleBeforeCopy}
          beforePaste={handleBeforePaste}
          afterChange={handleAfterChange}
          beforeOnCellMouseDown={handleBeforeOnCellMouseDown}
          beforeOnCellContextMenu={handleBeforeOnCellContextMenu}
          beforeChange={handleBeforeChange}
          contextMenu={contextMenuSettings as ExtendedContextMenuSettings | false}
          manualColumnResize={true}
          manualRowResize={true}
          autoRowSize={false}
          rowHeights={100}
          stretchH='all'
          autoWrapRow={true}
          autoWrapCol={true}
          fixedRowsTop={0}
          columnSorting={false}
          filters={false}
          dropdownMenu={true}
          allowInsertRow={true}
          allowInsertColumn={false}
          allowRemoveRow={true}
          autoColumnSize={true}
          allowRemoveColumn={false}
          outsideClickDeselects={false}
          selectionMode='multiple'
          currentRowClassName='current-row-highlight'
        />
      </div>
    </Paper>
  );
};
